import React from "react";
import { Easing, interpolate, spring, useCurrentFrame } from "remotion";
import { theme } from "../../brand";
import { COPY } from "../../b2c/copy";
import { FLOAT_SHADOW, LOOK } from "../../b2c/look";
import { FrakLogo } from "../../components/FrakLogo";
import { REEL, ReelFrame } from "../ui/ReelFrame";
import { SPLIT, SPLIT_GAIN } from "./copy";

/**
 * Reel "Même reco, deux fins" (9:16, silent, ~13s): a split screen, the
 * same story played twice in sync. Left (grey, sans Frak) and right (Frak
 * blue, avec Frak): you send your sister a serum, she buys it, she says
 * thanks. Only the outcome differs: 0 € / 0 € on the left; a (sample)
 * gain for you and cashback for her on the right. Then the blue side
 * takes over: the grey half is wiped away while the "avec Frak" column
 * glides to the centre with it, holds alone, and the end card says it:
 * "Frak paie les deux."
 *
 * Frame 0 is the cover (title, both headers, both first messages).
 * Silent: the sound is added in Instagram.
 */

/** Timeline, in frames. Holds are reading time: never shorten one. */
const T = {
  buy: 40,
  outcome: 90,
  /** The right side's values land a beat after the left's zeros. */
  gain: 98,
  thanks: 165,
  /**
   * Takeover: the grey half is wiped while the right column glides to the
   * centre with it (one move; a dim before it, then a drop out of frame,
   * both read as glitches). The winner holds alone, then the end card.
   */
  wipe: 240,
  wipeLen: 22,
  exit: 292,
  /** After the 10-frame hand-over: the end card never overlaps the column. */
  end: 303,
  duration: 399,
} as const;

export const SPLIT_DURATION = T.duration;

const GREEN = theme.brand.colors.success[800];
const money = COPY.fr.money;
const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/** Layout (logical px). Columns stay clear of the Reels rail on the right. */
const HALF = REEL.width / 2;
const COL = 410;
const CENTER = { without: 270, with: 780 } as const;
const Y = { title: 220, head: 470, send: 570, buy: 730, outcome: 880, thanks: 1200 };
const CARD_H = 270;

type Side = keyof typeof CENTER;

/** Springs in from `at` (scale + rise), identical in both columns. */
const Pop: React.FC<{ at: number; children: React.ReactNode; style?: React.CSSProperties }> = ({
  at,
  children,
  style,
}) => {
  const frame = useCurrentFrame();
  if (frame < at) return null;
  const p = spring({
    frame: frame - at,
    fps: REEL.fps,
    config: { damping: 14, stiffness: 220, mass: 0.6 },
  });
  return (
    <div
      style={{
        opacity: Math.min(1, p * 2),
        scale: 0.85 + 0.15 * p,
        translate: `0 ${(1 - p) * 30}px`,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const Bubble: React.FC<{ text: string; mine: boolean }> = ({ text, mine }) => (
  <div
    style={{
      alignSelf: mine ? "flex-end" : "flex-start",
      maxWidth: COL,
      padding: "20px 30px",
      borderRadius: 34,
      [mine ? "borderBottomRightRadius" : "borderBottomLeftRadius"]: 8,
      background: mine ? LOOK.white : LOOK.ink,
      color: mine ? LOOK.ink : LOOK.white,
      boxShadow: FLOAT_SHADOW,
      fontFamily: LOOK.body,
      fontWeight: LOOK.medium,
      fontSize: 44,
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </div>
);

const OutcomeRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 16,
    }}
  >
    <span
      style={{
        fontFamily: LOOK.body,
        fontWeight: LOOK.semi,
        fontSize: 34,
        color: LOOK.inkSoft,
      }}
    >
      {label}
    </span>
    {children}
  </div>
);

const Value: React.FC<{ at: number; color: string; size?: number; children: React.ReactNode }> = ({
  at,
  color,
  size = 64,
  children,
}) => {
  const frame = useCurrentFrame();
  const p = spring({
    frame: frame - at,
    fps: REEL.fps,
    config: { damping: 10, stiffness: 240, mass: 0.6 },
  });
  return (
    <span
      style={{
        display: "inline-block",
        fontFamily: LOOK.display,
        fontWeight: LOOK.bold,
        fontSize: size,
        letterSpacing: -1,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
        color,
        opacity: frame < at ? 0 : 1,
        scale: frame < at ? 0.6 : 0.6 + 0.4 * p,
        transformOrigin: "right center",
      }}
    >
      {children}
    </span>
  );
};

const Column: React.FC<{ side: Side }> = ({ side }) => {
  const frame = useCurrentFrame();
  const paid = side === "with";
  const gain = interpolate(frame, [T.gain, T.gain + 16], [0, SPLIT_GAIN], {
    ...clamp,
    easing: Easing.out(Easing.cubic),
  });
  const ink = paid ? LOOK.white : LOOK.inkSoft;

  return (
    <div
      style={{
        position: "absolute",
        left: CENTER[side] - COL / 2,
        top: 0,
        width: COL,
        height: REEL.height,
      }}
    >
      {/* Header */}
      <div
        style={{
          position: "absolute",
          top: Y.head,
          width: COL,
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 40,
          letterSpacing: 4,
          color: ink,
        }}
      >
        {paid ? (
          <>
            {SPLIT.with}
            <FrakLogo width={170} style={{ filter: "brightness(0) invert(1)" }} />
          </>
        ) : (
          SPLIT.without
        )}
      </div>

      {/* 1. You send the link (on screen from frame 0: the cover). */}
      <div style={{ position: "absolute", top: Y.send, width: COL, display: "flex", flexDirection: "column" }}>
        <Bubble text={SPLIT.send} mine />
      </div>

      {/* 2. She buys. */}
      <Pop at={T.buy} style={{ position: "absolute", top: Y.buy, width: COL, display: "flex", justifyContent: "center" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "16px 30px",
            borderRadius: 999,
            background: paid ? "rgba(255,255,255,0.16)" : LOOK.white,
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 46,
            color: paid ? LOOK.white : LOOK.ink,
          }}
        >
          <span style={{ fontSize: 50 }}>🛍️</span>
          {SPLIT.buy}
        </div>
      </Pop>

      {/* 3. The outcome: the only thing that differs. */}
      <Pop at={T.outcome} style={{ position: "absolute", top: Y.outcome, width: COL }}>
        <div
          style={{
            height: CARD_H,
            boxSizing: "border-box",
            padding: "26px 30px",
            borderRadius: 32,
            background: LOOK.white,
            boxShadow: FLOAT_SHADOW,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <OutcomeRow label={SPLIT.you}>
            {paid ? (
              <Value at={T.gain} color={GREEN}>
                +{money(gain)}
              </Value>
            ) : (
              <Value at={T.outcome + 4} color={LOOK.muted}>
                {SPLIT.zero}
              </Value>
            )}
          </OutcomeRow>
          <OutcomeRow label={SPLIT.her}>
            {paid ? (
              <Value at={T.gain + 8} color={GREEN} size={44}>
                {SPLIT.cashback}
              </Value>
            ) : (
              <Value at={T.outcome + 10} color={LOOK.muted}>
                {SPLIT.zero}
              </Value>
            )}
          </OutcomeRow>
          {paid ? (
            <div
              style={{
                marginTop: "auto",
                fontFamily: LOOK.body,
                fontSize: 24,
                lineHeight: 1.25,
                color: LOOK.inkSoft,
                opacity: frame < T.gain + 10 ? 0 : 1,
              }}
            >
              {SPLIT.note}
            </div>
          ) : null}
        </div>
      </Pop>

      {/* 4. Same thanks either way. */}
      <Pop at={T.thanks} style={{ position: "absolute", top: Y.thanks, width: COL, display: "flex", flexDirection: "column" }}>
        <Bubble text={SPLIT.thanks} mine={false} />
      </Pop>
    </div>
  );
};

export const SplitReel: React.FC = () => {
  const frame = useCurrentFrame();
  const wipe = interpolate(frame, [T.wipe, T.wipe + T.wipeLen], [0, 1], {
    ...clamp,
    easing: Easing.inOut(Easing.cubic),
  });
  // The column always stays right of the wipe edge (it moves 240px while
  // the edge moves 540px), so it never crosses the grey side's text.
  const glide = (REEL.width / 2 - CENTER.with) * wipe;
  // Hand-over to the end card: a short fade + settle, while the logo comes in.
  const out = interpolate(frame, [T.exit, T.exit + 10], [0, 1], {
    ...clamp,
    easing: Easing.in(Easing.quad),
  });
  const leaving: React.CSSProperties = { opacity: 1 - out, scale: 1 - 0.05 * out };

  return (
    <ReelFrame exitAt={T.exit} end={{ at: T.end, line: SPLIT.end.line, sub: SPLIT.end.sub }}>
      {/* Left half: grey, "sans Frak". Wiped away to the left at the end. */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: HALF * (1 - wipe),
          height: REEL.height,
          background: LOOK.greyBg,
          overflow: "hidden",
        }}
      >
        <Column side="without" />
      </div>

      <div style={{ position: "absolute", inset: 0, translate: `${glide}px 0`, ...leaving }}>
        <Column side="with" />
      </div>

      {/* Title, across the split. */}
      <div
        style={{
          position: "absolute",
          top: Y.title,
          left: 90,
          right: 90,
          padding: "30px 40px 34px",
          borderRadius: 36,
          background: LOOK.white,
          boxShadow: FLOAT_SHADOW,
          textAlign: "center",
          ...leaving,
        }}
      >
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 68,
            letterSpacing: -2,
            lineHeight: 1.05,
            color: LOOK.ink,
          }}
        >
          {SPLIT.title}
        </div>
        <div
          style={{
            marginTop: 10,
            fontFamily: LOOK.body,
            fontWeight: LOOK.medium,
            fontSize: 34,
            color: LOOK.inkSoft,
          }}
        >
          {SPLIT.setup}
        </div>
      </div>
    </ReelFrame>
  );
};
